import { apiFetch } from './client'
import type { FacetResponse } from '../components/product/filters/types'

/**
 * Filter query shared with `/api/v1/products` — must serialize identically
 * so both endpoints see the same criteria (STORY-006). Names are the
 * URL-shared scheme that the `<ProductFilters>` component already writes.
 */
export interface FacetsQuery {
  category?: string
  subcategory?: string
  series?: string
  search?: string
  labels?: string[]
  /** CSV or single value — `ProductFilterCriteria::fromRequest` accepts both. */
  lifecycle_phase?: string
  /** 3-state legacy (uitloop) visibility: 'all' (default), 'hide', 'only'. */
  legacy?: string
  price_range?: string
  /** CSV of stock statuses, e.g. `in_stock,on_order`. */
  stock_status?: string
  delivery_speed?: string
  brand?: string
  /**
   * Per-category attribute filters — same shape and wire format as
   * `ProductQueryParams.attributes`. Both endpoints must see the same
   * criteria so the facet counts reflect what the product list shows.
   */
  attributes?: Record<string, readonly string[]>
}

export async function fetchProductFacets(
  params: FacetsQuery = {},
): Promise<FacetResponse> {
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.subcategory) qs.set('subcategory', params.subcategory)
  if (params.series) qs.set('series', params.series)
  if (params.search) qs.set('search', params.search)
  if (params.labels && params.labels.length > 0) {
    params.labels.forEach((l) => qs.append('labels', l))
  }
  if (params.lifecycle_phase) qs.set('lifecycle_phase', params.lifecycle_phase)
  if (params.legacy && params.legacy !== 'all') qs.set('legacy', params.legacy)
  if (params.price_range) qs.set('price_range', params.price_range)
  if (params.stock_status) qs.set('stock_status', params.stock_status)
  if (params.delivery_speed) qs.set('delivery_speed', params.delivery_speed)
  if (params.brand) qs.set('brand', params.brand)
  if (params.attributes) {
    for (const [attrKey, values] of Object.entries(params.attributes)) {
      if (!values || values.length === 0) continue
      const paramKey = `attribute[${attrKey}][]`
      for (const v of values) {
        if (!v) continue
        qs.append(paramKey, v)
      }
    }
  }

  const query = qs.toString()
  // Singleton API Platform resource — `WrappedResponseNormalizer` wraps
  // the single object in `{ data: ... }`.
  const res = await apiFetch<{ data: FacetResponse }>(
    `/products/facets${query ? `?${query}` : ''}`,
  )
  return res.data
}
